import React, { ReactElement, useMemo, useRef, useState } from 'react'
import { Currency, defaultCurrencyCode } from 'store/api/billing'
import { setSelectedCurrency } from 'store/billing'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { Flex, Text } from 'theme-ui'
import { Dropdown, SimpleActionMenu, Svg } from 'ui'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import ArrowDownIcon from 'images/organization/homepage/headerNavMenu/arrow-sm-down.svg'
import ExternalSvgIcon, { IconType } from 'components/Dashboard/CommonComponents/ExternalSvgIcon'

export default function CurrencyDropdown(): ReactElement {
  const hideRef = useRef<() => void>()
  const dispatch = useAppDispatch()
  const isMobile = useBreakpointIndex() === 0
  const currencies = useAppSelector(s => s.products.currencies)
  const selectedCurrency = useAppSelector(s => s.products.selectedCurrency)
  const [isOpen, setIsOpen] = useState(false)
  const currencyItems = useMemo(
    () =>
      currencies?.map(currency => {
        const isSelected = currency.code === (selectedCurrency?.code ?? defaultCurrencyCode)
        return {
          sx: {
            opacity: 1,
            ...(isSelected
              ? {
                  borderColor: 'littleBoyBlue',
                }
              : {}),
          },
          ariaLabel: `${currency.name} option`,
          isSelected,
          onClick: () => {
            dispatch(setSelectedCurrency(currency))
            hideRef.current?.()
            if (isMobile) {
              window.scrollTo({ top: 0 })
            }
          },
          children: <CurrencyItem currency={currency} />,
          tabIndex: 0,
        }
      }) ?? [],
    [currencies, dispatch, isMobile, selectedCurrency?.code],
  )
  const dropdowncontent = useMemo(
    () => (
      <SimpleActionMenu
        hideRef={hideRef}
        isOpen={isOpen}
        items={currencyItems}
        sxContainer={{
          width: ['100%', '28.1rem'],
          gap: '0.1rem',
          px: 0,
          py: '0.4rem',
        }}
        sxButton={{
          py: '0.4rem',
          px: '0.8rem',
          borderRadius: '0.8rem',
          svg: {
            opacity: 1,
          },
        }}
      />
    ),
    [currencyItems, isOpen],
  )

  return (
    <Flex
      sx={{ flex: 1, position: 'relative', justifyContent: 'flex-end' }}
      className={isMobile ? 'reset-tippy-width' : ''}
    >
      <Dropdown
        variant="roundedSecondary"
        data-testid="select-currency-dropdown"
        ariaLabel="select currency dropdown"
        tippyprops={{
          animation: 'fade',
          duration: 300,
          onCreate: instance => {
            hideRef.current = () => {
              instance.hide()
            }
          },
          onHide: () => {
            setIsOpen(false)
          },
          onShow: () => {
            setIsOpen(true)
          },
          placement: 'bottom-end',
          offset: [0, 8],
          theme: 'orgs-homepage',
          arrow: true,
          maxWidth: '100%',
        }}
        sx={{
          px: 0,
          fontSize: '1.7rem',
          fontWeight: 500,
          '> div > svg': {
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            fill: isOpen ? 'aliceBlue' : 'aliceBlue60',
          },
        }}
        dropdowncontent={dropdowncontent}
      >
        <Flex
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            flex: '1',
            px: '1.2rem',
          }}
        >
          <CurrencyItem currency={selectedCurrency} isButton />
          <Svg
            svg={ArrowDownIcon}
            sx={{
              ml: '0.8rem',
              flexShrink: 0,
            }}
            descriptiveText="Currency dropdown arrow"
          />
        </Flex>
      </Dropdown>
    </Flex>
  )
}

const CurrencyItem = ({
  currency,
  isButton = false,
}: {
  currency?: Currency
  isButton?: boolean
}) => {
  return (
    <Flex
      sx={{
        flex: 1,
        alignItems: 'center',
        textAlign: 'left',
        justifyContent: 'flex-start',
      }}
    >
      <ExternalSvgIcon
        className="flag"
        icon={currency?.country_code || 'US'}
        type={IconType.LOCATIONS}
        sx={{
          width: '1.6rem',
          height: '1.6rem',
          borderRadius: '100px',
          mr: '0.8rem',
        }}
        descriptiveText={`${currency?.name || 'US'} flag`}
      />

      <Text
        className="text"
        variant="size17Weight600Line138"
        sx={{
          width: '3.4rem',
          color: 'aliceBlue',
          fontWeight: 'normal',
          flexGrow: 0,
        }}
      >
        {currency?.code.toUpperCase() ?? 'USD'}
      </Text>
      {!isButton && (
        <Text
          variant="size16Weight500Line138"
          className="description"
          sx={{
            textAlign: 'left',
            ml: '1.6rem',
            fontWeight: 'normal',
            color: 'aliceBlue70',
          }}
        >
          {currency?.name}
        </Text>
      )}
    </Flex>
  )
}
